import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';

import defaultCarImage from '../../../assets/images/default-car-image-autosrelif.jpeg';
import { type AutosRelifCar, useGetAutosRelifCarsQuery } from '../../../services/api/autosrelif.api';
import AutosRelifDetailSection from './CarDetails';

const DEFAULT_IMAGE = defaultCarImage;
const BASE_COLOR = '#021024';
const ACCENT_COLOR = '#FFCC00';

const AutosRelifLandingPage = () => {
  const { data } = useGetAutosRelifCarsQuery();

  const [selectedCar, setSelectedCar] = useState<AutosRelifCar | null>(null);
  const [success, setSuccess] = useState(false);
  const [activeCar, setActiveCar] = useState<AutosRelifCar | null>(null);
  const previousScrollPosition = useRef<number>(0);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    rut: '',
    email: '',
  });

  const newCars: AutosRelifCar[] = data?.newCars ?? [];
  const usedCars: AutosRelifCar[] = data?.usedCars ?? [];
  const brands = [...new Set(newCars.map((car) => car.brand))].sort();

  const handleSubmitInterest = async () => {
    if (!selectedCar) return;

    await fetch('/autosrelif/create-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form,
        brandOfInterest: selectedCar.brand,
        modelOfInterest: selectedCar.model,
      }),
    });

    setSuccess(true);
    setTimeout(() => {
      setSelectedCar(null);
      setSuccess(false);
    }, 2500);
  };

  useEffect(() => {
    if (activeCar) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (previousScrollPosition.current > 0) {
      window.scrollTo({ top: previousScrollPosition.current, behavior: 'smooth' });
      previousScrollPosition.current = 0;
    }
  }, [activeCar]);

  const renderCarCard = (car: AutosRelifCar) => {
    const title = `${car.brand} ${car.model}`;
    const price = car.price ? `$${car.price.toLocaleString('es-CL')}` : 'Consultar precio';

    return (
      <Box
        key={car.id}
        sx={{
          borderRadius: 2,
          boxShadow: 3,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          component="img"
          src={car.image || DEFAULT_IMAGE}
          alt={title}
          sx={{ height: 180, objectFit: 'cover' }}
          onError={(e) => {
            (e.target as HTMLImageElement).src = DEFAULT_IMAGE;
          }}
        />
        <Box
          sx={{
            p: 2,
            flexGrow: 1,
          }}
        >
          <Typography variant="h6">{title}</Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {car.km ? `${car.km.toLocaleString('es-CL')} km` : (car.version ?? 'Versión estándar')}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mt: 1,
            }}
          >
            {price}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={() => setSelectedCar(car)}
            sx={{ backgroundColor: BASE_COLOR, '&:hover': { backgroundColor: ACCENT_COLOR } }}
          >
            Me interesa
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              previousScrollPosition.current = window.scrollY;
              setActiveCar((prev) => (prev?.id === car.id ? null : car));
            }}
            sx={{ color: BASE_COLOR, borderColor: BASE_COLOR, '&:hover': { borderColor: ACCENT_COLOR } }}
          >
            Ver detalles
          </Button>
        </Box>
      </Box>
    );
  };

  const headerStyle = {
    color: '#fff',
    textDecoration: 'none',
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: '20px',
  };

  return (
    <>
      {/* HEADER */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: BASE_COLOR,
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}
      >
        <Box
          sx={{
            maxWidth: '1200px',
            mx: 'auto',
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#fff',
          }}
        >
          <Typography
            sx={{
              fontSize: 32,
              fontWeight: 700,
              fontFamily: "'Bebas Neue', sans-serif",
            }}
          >
            AutosRelif
          </Typography>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 3,
            }}
          >
            <a href="#nuevos" style={headerStyle}>
              Autos Nuevos
            </a>
            <a href="#usados" style={headerStyle}>
              Autos Usados
            </a>
            <a href="#marcas" style={headerStyle}>
              Marcas
            </a>
            <a href="#contacto" style={headerStyle}>
              Contacto
            </a>
          </Box>
        </Box>
      </Box>
      {/* ACTIVE CAR DETAILS */}
      <Box id="car-detail">
        {activeCar && <AutosRelifDetailSection car={activeCar} onClose={() => setActiveCar(null)} />}
      </Box>
      <Box
        sx={{
          height: '80vh',
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1600')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            maxWidth: 800,
            px: 3,
          }}
        >
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: 900,
              fontFamily: "'Source Sans Pro', sans-serif",
              fontSize: '42px',
            }}
          >
            TU PRÓXIMO AUTO TE ESTÁ ESPERANDO
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mb: 4,
            }}
          >
            Encuentra el vehículo perfecto entre nuestra amplia selección de autos nuevos y usados
          </Typography>

          <Button
            href="#contacto"
            sx={{
              background: '#25d366',
              color: '#fff',
              px: 4,
              py: 1.5,
              borderRadius: 999,
              fontSize: 18,
              fontWeight: 700,
              '&:hover': { background: '#1da851' },
            }}
          >
            Contáctanos por WhatsApp
          </Button>
        </Box>
      </Box>
      {/* AUTOS NUEVOS */}
      <Box
        id="nuevos"
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          px: 3,
          py: 8,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            mb: 1,
            fontFamily: "'Bebas Neue', sans-serif",
          }}
        >
          Autos Nuevos
        </Typography>

        <Typography
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 5,
          }}
        >
          Descubre las últimas novedades de las mejores marcas
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 3,
          }}
        >
          {newCars.length === 0 && <Typography>No hay autos nuevos</Typography>}
          {newCars.map((car) => renderCarCard(car))}
        </Box>
      </Box>
      {/* AUTOS USADOS */}
      <Box
        id="usados"
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          px: 3,
          py: 8,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            mb: 1,
            fontFamily: "'Bebas Neue', sans-serif",
          }}
        >
          Autos Usados
        </Typography>

        <Typography
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 5,
          }}
        >
          Descubre las últimas novedades de las mejores marcas
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 3,
          }}
        >
          {usedCars.length === 0 && <Typography>No hay autos usados</Typography>}
          {usedCars.map((car) => renderCarCard(car))}
        </Box>
      </Box>
      {/* INTEREST MODAL */}
      <Modal open={!!selectedCar} onClose={() => setSelectedCar(null)}>
        <Box
          sx={{
            background: '#fff',
            p: 4,
            borderRadius: 2,
            width: 400,
            mx: 'auto',
            mt: '10%',
          }}
        >
          {success ? (
            <Alert severity="success">¡Te contactaremos pronto!</Alert>
          ) : (
            <form
              id="interest-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmitInterest().catch(() => {});
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                }}
              >
                Me interesa {selectedCar?.brand} {selectedCar?.model}
              </Typography>

              <TextField
                fullWidth
                name="name"
                label="Nombre"
                margin="dense"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                required
              />

              <TextField
                fullWidth
                name="lastName"
                label="Apellido"
                margin="dense"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              />

              <TextField
                fullWidth
                name="phone"
                label="Teléfono"
                margin="dense"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
              />

              <TextField
                fullWidth
                name="rut"
                label="RUT"
                margin="dense"
                value={form.rut}
                onChange={(e) => setForm({ ...form, rut: e.target.value })}
              />

              <TextField
                fullWidth
                name="email"
                type="email"
                label="Email"
                margin="dense"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />

              {selectedCar && (
                <>
                  <input type="hidden" name="brand" value={selectedCar.brand || undefined} />
                  <input type="hidden" name="model" value={selectedCar.model || undefined} />
                </>
              )}

              <Button
                fullWidth
                type="submit"
                variant="contained"
                sx={{ mt: 2, backgroundColor: BASE_COLOR, '&:hover': { backgroundColor: ACCENT_COLOR } }}
              >
                Enviar interés
              </Button>
            </form>
          )}
        </Box>
      </Modal>
      {/* MARCAS */}
      <Box id="marcas" sx={{ background: '#f7fafc', py: 8 }}>
        <Box
          sx={{
            maxWidth: '1200px',
            mx: 'auto',
            px: 3,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              mb: 1,
              fontFamily: "'Bebas Neue', sans-serif",
            }}
          >
            Nuestras Marcas
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              mb: 5,
            }}
          >
            Representantes oficiales de las mejores marcas del mercado
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 4,
            }}
          >
            {brands.map((brand) => (
              <Box
                key={brand}
                sx={{
                  background: '#fff',
                  px: 4,
                  py: 2,
                  borderRadius: 2,
                  boxShadow: 1,
                  fontWeight: 700,
                }}
              >
                {brand}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      {/* CONTACTO */}
      <Box
        id="contacto"
        sx={{
          background: BASE_COLOR,
          color: '#fff',
          textAlign: 'center',
          py: 8,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            mb: 2,
            fontFamily: "'Bebas Neue', sans-serif",
          }}
        >
          ¿Listo para tu nuevo auto?
        </Typography>

        <Typography
          sx={{
            mb: 4,
          }}
        >
          Contáctanos y uno de nuestros ejecutivos te asesorará personalmente
        </Typography>

        <Button
          href="https://wa.me/56993057973?text=Hola,%20quiero%20m%C3%A1s%20informaci%C3%B3n"
          target="_blank"
          sx={{
            background: '#25d366',
            color: '#fff',
            px: 4,
            py: 1.5,
            fontWeight: 700,
            '&:hover': { background: '#1da851' },
          }}
        >
          Chatear por WhatsApp
        </Button>
      </Box>
    </>
  );
};

export default AutosRelifLandingPage;
